import React, {useState, useMemo, useEffect, useCallback} from 'react'
import {toast} from 'react-toastify'
import {saveAs} from 'file-saver'
import * as XLSX from 'xlsx'
import moment from 'moment'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import MetaTags from '../../../../meta/MetaTags'
import DataTableList from '../../components/DataTableList'
import {DOCTORS_BILLING_REPORT_HEADERS} from '../../../../constants/RoleConstants'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import {useBillingReport} from '../../../../hooks/useBillingReport'
import {useReduxAuth} from '../../../auth'
import FilterComponent from '../../components/filters/FilterComonent'
import * as Yup from 'yup'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import { useBillingDropdown } from '../../context/BillingDropdownContext'
import { useMADoctorDropdown } from '../../context/MADoctorDropdownContext'

// Stable noop function to prevent useEffect dependency changes
const noop = () => {}

const BillingReport: React.FC = () => {

  const {currentUser} = useReduxAuth()
  // const [showBillingPersonnelModal, setShowBillingPersonnelModal] = useState(false)

  const { selectedDoctorId, setSelectedDoctorId } = useBillingDropdown()
  // Always call hook
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  const setMaSelectedDoctorId = currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.setSelectedDoctorId : noop

  // 1) Filter state
  const [viewType, setViewType] = useState<'weekly' | 'monthly'>('monthly')
  const [monthYear, setMonthYear] = useState(() => moment().format('MMMM YYYY'))

  // 2) Doctor dropdown state
  const [doctors, setDoctors] = useState<
    Array<{id: number; uuid: string; first_name: string; last_name: string}>
  >([])


  const doctorFilter =
    currentUser.role_id === USER_ROLES.BILLING_PERSONNEL
      ? selectedDoctorId
      : currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT
      ? maSelectedDoctorId
      : currentUser.id

  const {data, loading, error, fetchReport, toggleStatus} = useBillingReport(
    monthYear,
    doctorFilter
  )

  // 4) Confirmation modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [pendingRow, setPendingRow] = useState<any | null>(null)
  const [statusNotes, setStatusNotes] = useState<string>('')

  const [filteredData, setFilteredData] = useState<any>([])

  useEffect(() => {
    if (currentUser.role_id === USER_ROLES.BILLING_PERSONNEL) {
      axios
        .get(API_ENDPOINTS.GET_DOCTORS_FOR_BILLING_PERSONNEL(currentUser.id.toString()))
        .then((resp) => {
        const fetchedDoctors = resp.data.data.doctors || [];
        setDoctors(fetchedDoctors);

          if (fetchedDoctors.length > 0) {
            const exists = fetchedDoctors.some((d: { id: number }) => d.id === selectedDoctorId);
            if (!selectedDoctorId || !exists) {
              setSelectedDoctorId(fetchedDoctors[0].id);
            }
          } else {
            setSelectedDoctorId(undefined);
          }

      })
        .catch((err) => {
          // Clear doctors on error
          setDoctors([])
          setSelectedDoctorId(undefined)
          toast.error('Failed to load affiliated doctors for billing personnel')
        })
    } else if (currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT) {
      // MA can access all doctors via dropdown
      axios
        .get(API_ENDPOINTS.GET_ALL_DOCTORS())
        .then((resp) => {
          const fetchedDoctors = resp.data.data?.doctors || resp.data.data || []
          setDoctors(fetchedDoctors)

          if (fetchedDoctors.length > 0) {
            const exists = fetchedDoctors.some((d: { id: number }) => d.id === maSelectedDoctorId)
            if (!maSelectedDoctorId || !exists) {
              setMaSelectedDoctorId(fetchedDoctors[0].id)
            }
          } else {
            setMaSelectedDoctorId(undefined)
          }
        })
        .catch((err) => {
          toast.error('Failed to load doctors')
          setDoctors([])
          setMaSelectedDoctorId(undefined)
        })
    }
  }, [currentUser, selectedDoctorId, maSelectedDoctorId, setSelectedDoctorId, setMaSelectedDoctorId])

  //  Skip fetch if no doctor selected
  useEffect(() => {
    if ((currentUser.role_id === USER_ROLES.BILLING_PERSONNEL && !selectedDoctorId) ||
        (currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT && !maSelectedDoctorId)) {
      setFilteredData([])
      return
    }
  }, [selectedDoctorId, maSelectedDoctorId, currentUser.role_id])


  // Clear doctor dropdown when 403 error occurs in billing report
  useEffect(() => {
    if (
      (error && error.toLowerCase().includes('403') ||
        error && (error.toLowerCase().includes('billing personnel') ||
          error.toLowerCase().includes('not linked') ||
          error.toLowerCase().includes('forbidden')
        ))
    ) {
      // Clear doctors dropdown since access has been revoked
      setDoctors([])
      setSelectedDoctorId(undefined)
    }
  }, [error])

  // Re-fetch report when a different doctor is selected
  useEffect(() => {
    if ((currentUser.role_id === USER_ROLES.BILLING_PERSONNEL && selectedDoctorId) ||
        (currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId)) {
      fetchReport(monthYear)
    }
  }, [selectedDoctorId, maSelectedDoctorId, fetchReport, monthYear, currentUser.role_id])

  // 7) Filter change handler
  const onFilterChange = useCallback(
    (_view: 'weekly' | 'monthly', start: moment.Moment) => {
      const next = start.format('MMMM YYYY')
      setViewType(_view)
      setMonthYear(next)
      fetchReport(next)
    },
    [fetchReport]
  )

  // Status toggle handlers
  const handleStatusToggle = (row: any) => {
    setPendingRow(row)
    setStatusNotes('')
    setConfirmModalOpen(true)
  }

  const confirmToggle = async () => {
    if (!pendingRow) return
    setConfirmModalOpen(false)
    try {
      await toggleStatus({...pendingRow, notes: statusNotes})
      toast.success('Status updated')
      fetchReport(monthYear)
    } catch {
      toast.error('Update failed')
    } finally {
      setPendingRow(null)
      setStatusNotes('')
    }
  }
  useEffect(() => {
    getDataFilteredMonthly(data, monthYear)
  }, [data, monthYear])

  const getDataFilteredMonthly = (data: any[], month: string) => {
    const result = data.filter((item: any) => {
      const cycleStart = item.cycle_start ? moment(item.cycle_start) : null;
      const cycleEnd = item.cycle_end ? moment(item.cycle_end) : null;

      // Check both start and end month
      const startsInMonth = cycleStart && cycleStart.format('MMMM YYYY') === month;
      const endsInMonth = cycleEnd && cycleEnd.format('MMMM YYYY') === month;

      return startsInMonth || endsInMonth;
    });

    setFilteredData(result);
  };

  const exportData = useMemo(() => {
    const excludedColumns: string[] = []

    const filteredHeaders = DOCTORS_BILLING_REPORT_HEADERS.filter(
      (col) => !excludedColumns.includes(col.name)
    )

    return filteredData.map((row: any) => {
      const rowData: Record<string, any> = {}

      filteredHeaders.forEach((col) => {
        rowData[col.name] =
          typeof col.selector === 'function' ? col.selector(row) : (row as any)[col.key]
      })

      return rowData
    })
  }, [filteredData])

  const exportToCSV = useCallback(() => {
    try {
      const ws = XLSX.utils.json_to_sheet(exportData)
      const csv = XLSX.utils.sheet_to_csv(ws)

      // Format monthYear (e.g., "May 2025") to "05_25"
      const formattedDate = moment(monthYear, 'MMMM YYYY').format('MM_YY')

      const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'})
      saveAs(blob, `CandiHealth_${formattedDate}.csv`)
      toast.success('CSV exported successfully!')
    } catch {
      toast.error('CSV export failed')
    }
  }, [exportData, monthYear])

  const cancelToggle = () => {
    setConfirmModalOpen(false)
    setPendingRow(null)
    setStatusNotes('')
  }

  // Calculate if we should show loading
  // - For doctors: show loading when the hook is loading
  // - For billing personnel/MA: show loading when hook is loading AND a doctor is selected AND doctors are available
  // - Don't show loading if billing personnel/MA has no doctors available or none selected
  const shouldShowLoading = (() => {
    if (currentUser.role_id === USER_ROLES.BILLING_PERSONNEL || currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT) {
      // If no doctors available, don't show loading
      if (doctors.length === 0) return false
      // If no doctor selected, don't show loading
      const selectedId = currentUser.role_id === USER_ROLES.BILLING_PERSONNEL ? selectedDoctorId : maSelectedDoctorId
      if (!selectedId) return false
      // Otherwise show loading when the hook is loading
      return loading
    }
    // For other roles (like doctors), show loading when hook is loading
    return loading
  })()
  return (
    <>
      <MetaTags title='Billing Report' />

      {/* Invite or Select Doctor & Filter */}
      <div className='d-flex justify-content-end align-items-center mb-2'>
        {(currentUser.role_id === USER_ROLES.BILLING_PERSONNEL || currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT) && (
          <div className='w-25'>
            <label htmlFor='doctorSelect' className='form-label'>
              Select Doctor
            </label>
            <select
              id='doctorSelect'
              className='form-select'
              value={currentUser.role_id === USER_ROLES.BILLING_PERSONNEL ? (selectedDoctorId ?? '') : (maSelectedDoctorId ?? '')}
              onChange={(e) => {
                const val = e.target.value
                if (currentUser.role_id === USER_ROLES.BILLING_PERSONNEL) {
                  setSelectedDoctorId(val ? Number(val) : undefined)
                } else {
                  setMaSelectedDoctorId(val ? Number(val) : undefined)
                }
              }}
              disabled={doctors.length === 0}
            >
              {doctors.length === 0 ? (
                <option value=''>-- No doctors available --</option>
              ) : (
                <>
                  <option value=''>-- Select Doctor --</option>
                  {doctors.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.first_name} {d.last_name}
                    </option>
                  ))}
                </>
              )}
            </select>
            {doctors.length === 0 && error && (
              <div className='mt-2'>

              </div>
            )}
          </div>
        )}
        <div className='d-flex flex-column align-items-center'>
          <FilterComponent
            startDate={moment(monthYear, 'MMMM YYYY')}
            endDate={moment(monthYear, 'MMMM YYYY').endOf('month')}
            viewType={viewType}
            showDurationButton={false}
            onFilterChange={onFilterChange}
          />
        </div>
      </div>

      {/* Only show error if it's not a 403/access-related error */}
      {error && !(
        error.toLowerCase().includes('403') || 
        error.toLowerCase().includes('billing personnel') || 
        error.toLowerCase().includes('not linked') ||
        error.toLowerCase().includes('forbidden')
      ) && <div className='text-danger'>{error}</div>}



      {/* Note */}
      <div className="bg-light text-dark d-flex align-items-center border rounded p-3 mb-3">
        <span className="me-2 text-primary">
          <i className="bi bi-info-circle-fill" style={{ fontSize: '1.3rem' }}></i>
        </span>
        <div>
          <strong>Note:</strong> You may see multiple entries for patients whose cycle ends and another starts within the same month.
        </div>
      </div>


      {/* Confirmation modal for status toggle */}
      <ConfirmationModal
        isOpen={confirmModalOpen}
        title='Confirm Status Change'
        message='Please provide a reason for changing this patient status.'
        confirmButtonTitle='Yes, Change Status'
        requireNotes={true}
        notes={statusNotes}
        onNotesChange={setStatusNotes}
        onCancel={cancelToggle}
        onConfirm={confirmToggle}
      />

      <DataTableList
        title='Billing Report'
        columns={DOCTORS_BILLING_REPORT_HEADERS}
        data={filteredData}
        isLoading={shouldShowLoading}
        showAddButton={false}
        showExportButton={filteredData.length > 0 && !shouldShowLoading}
        onExportClick={exportToCSV}
        handleStatusToggle={handleStatusToggle}
        allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.BILLING_PERSONNEL, USER_ROLES.MEDICAL_ASSISTANT]}
        footerNote={
          <div className='rounded-2 px-3 py-2' style={{backgroundColor: '#FFF8E1'}}>
            <small style={{color: '#5f6368'}}>
              <span className='text-warning fw-bold'>*</span> Requires provider review for patient stability before billing
            </small>
          </div>
        }
      />

      {/* 2026 Billing Codes Guide */}
      <div className='bg-white rounded-3 border shadow-sm mt-4 mb-4 p-4'>
        {/* New 2026 RPM Codes Intro */}
        <div className='rounded-3 p-4 mb-4' style={{backgroundColor: '#EDEFFD'}}>
          <div className='d-flex align-items-start mb-3'>
            <span className='me-2 mt-1'>
              <i className='bi bi-info-circle-fill text-success' style={{fontSize: '1.4rem'}}></i>
            </span>
            <div>
              <h6 className='fw-bold mb-1'>
                <i className='bi bi-rocket-takeoff me-1'></i>
                New 2026 RPM Codes Now Active
              </h6>
              <span className='text-secondary' style={{fontSize: '0.875rem'}}>
                Two new codes effective January 1, 2026 close gaps for patients with shorter
                monitoring periods or less intensive care:
              </span>
            </div>
          </div>
          <div className='row g-3 ms-4 ps-2'>
            <div className='col-md-6'>
              <div className='bg-white rounded-3 py-3 px-4 border-start border-4 border-success'>
                <div className='fw-bold text-success' style={{fontSize: '0.95rem'}}>CPT 99445 (~$47)</div>
                <small className='text-secondary'>Device supply with 2-15 days of readings</small>
              </div>
            </div>
            <div className='col-md-6'>
              <div className='bg-white rounded-3 py-3 px-4 border-start border-4 border-danger'>
                <div className='fw-bold text-danger' style={{fontSize: '0.95rem'}}>CPT 99470 (~$26)</div>
                <small className='text-secondary'>Clinical time: 10-19 minutes</small>
              </div>
            </div>
          </div>
        </div>

        <h5 className='fw-bold mb-1'>
          <i className='bi bi-journal-text me-2'></i>
          2026 Billing Codes Guide
        </h5>
        <div className='row mt-3'>
          {/* Left Column - Device Supply Codes */}
          <div className='col-lg-6 mb-3 mb-lg-0'>
            <h6 className='fw-bold text-dark mb-3'>Device Supply Codes (Either/Or)</h6>

            {/* CPT 99445 */}
            <div className='rounded-3 p-3 px-4 mb-3' style={{backgroundColor: '#F8F9FA', border: '1px solid #E9ECEF'}}>
              <div className='d-flex align-items-center mb-2'>
                <span className='badge rounded-pill me-2 px-2 py-1' style={{backgroundColor: '#198754', fontSize: '0.7rem'}}>
                  NEW 2026
                </span>
                <span className='fw-bold' style={{fontSize: '0.95rem'}}>CPT 99445</span>
              </div>
              <p className='text-secondary mb-2' style={{fontSize: '0.85rem'}}>
                Device supply with daily recording or alert transmission
              </p>
              <div className='d-flex justify-content-between align-items-center'>
                <span className='d-flex align-items-center gap-1'>
                  <span className='text-success fw-bold' style={{fontSize: '0.85rem'}}>2-15 days</span>
                  <OverlayTrigger
                    placement='top'
                    overlay={
                      <Tooltip>
                        Use when shorter monitoring is clinically appropriate (e.g., post-discharge, medication titration, stable chronic conditions). Document medical necessity for the monitoring frequency.
                      </Tooltip>
                    }
                  >
                    <i className='bi bi-info-circle text-secondary' style={{fontSize: '0.8rem', cursor: 'pointer'}}></i>
                  </OverlayTrigger>
                </span>
                <span className='fw-bold text-dark' style={{fontSize: '1.05rem'}}>~$47</span>
              </div>
            </div>

            {/* CPT 99454 */}
            <div className='rounded-3 p-3 px-4 mb-3' style={{backgroundColor: '#F8F9FA', border: '1px solid #E9ECEF'}}>
              <div className='d-flex align-items-center mb-2'>
                <span className='badge rounded-pill me-2 px-2 py-1' style={{backgroundColor: '#2D6A4F', fontSize: '0.7rem'}}>
                  EXISTING
                </span>
                <span className='fw-bold' style={{fontSize: '0.95rem'}}>CPT 99454</span>
              </div>
              <p className='text-secondary mb-2' style={{fontSize: '0.85rem'}}>
                Device supply with daily recording or alert transmission
              </p>
              <div className='d-flex justify-content-between align-items-center'>
                <span className='text-success fw-bold' style={{fontSize: '0.85rem'}}>16-30 days</span>
                <span className='fw-bold text-dark' style={{fontSize: '1.05rem'}}>~$47</span>
              </div>
            </div>

            {/* Rule Note */}
            <div className='rounded-3 p-3 px-4' style={{backgroundColor: '#FEF0E7'}}>
              <small>
                <span className='text-danger fw-bold'>Rule:</span>{' '}
                <span className='text-secondary'>Cannot bill both 99445 and 99454 in the same month. Based on number of days with readings.</span>
              </small>
            </div>
          </div>

          {/* Right Column - Care Management Codes */}
          <div className='col-lg-6'>
            <h6 className='fw-bold text-dark mb-3'>
              Care Management Codes (Mutually Exclusive)
            </h6>

            {/* CPT 99470 */}
            <div className='rounded-3 p-3 px-4 mb-3' style={{backgroundColor: '#F8F9FA', border: '1px solid #E9ECEF'}}>
              <div className='d-flex align-items-center mb-2'>
                <span className='badge rounded-pill me-2 px-2 py-1' style={{backgroundColor: '#dc3545', fontSize: '0.7rem'}}>
                  NEW 2026
                </span>
                <span className='fw-bold' style={{fontSize: '0.95rem'}}>CPT 99470</span>
              </div>
              <p className='text-secondary mb-2' style={{fontSize: '0.85rem'}}>
                Clinical staff/physician time with &ge;1 interactive communication
              </p>
              <div className='d-flex justify-content-between align-items-center'>
                <span className='text-danger fw-bold' style={{fontSize: '0.85rem'}}>10-19 minutes</span>
                <span className='fw-bold text-dark' style={{fontSize: '1.05rem'}}>~$26</span>
              </div>
            </div>

            {/* CPT 99457 */}
            <div className='rounded-3 p-3 px-4 mb-3' style={{backgroundColor: '#F8F9FA', border: '1px solid #E9ECEF'}}>
              <div className='d-flex align-items-center mb-2'>
                <span className='badge rounded-pill me-2 px-2 py-1' style={{backgroundColor: '#2D6A4F', fontSize: '0.7rem'}}>
                  EXISTING
                </span>
                <span className='fw-bold' style={{fontSize: '0.95rem'}}>CPT 99457</span>
              </div>
              <p className='text-secondary mb-2' style={{fontSize: '0.85rem'}}>
                First 20 minutes of remote monitoring treatment management
              </p>
              <div className='d-flex justify-content-between align-items-center'>
                <span className='text-success fw-bold' style={{fontSize: '0.85rem'}}>&ge;20 minutes</span>
                <span className='fw-bold text-dark' style={{fontSize: '1.05rem'}}>~$52</span>
              </div>
            </div>

            {/* CPT 99458 */}
            <div className='rounded-3 p-3 px-4 mb-3' style={{backgroundColor: '#F8F9FA', border: '1px solid #E9ECEF'}}>
              <div className='d-flex align-items-center mb-2'>
                <span className='badge rounded-pill me-2 px-2 py-1' style={{backgroundColor: '#2D6A4F', fontSize: '0.7rem'}}>
                  EXISTING
                </span>
                <span className='fw-bold' style={{fontSize: '0.95rem'}}>CPT 99458</span>
              </div>
              <p className='text-secondary mb-2' style={{fontSize: '0.85rem'}}>
                Each additional 20 minutes beyond first 20
              </p>
              <div className='d-flex justify-content-between align-items-center'>
                <span className='text-success fw-bold' style={{fontSize: '0.85rem'}}>Per 20-min (max 3)</span>
                <span className='fw-bold text-dark' style={{fontSize: '1.05rem'}}>~$41/unit</span>
              </div>
            </div>

            {/* Rule Note */}
            <div className='rounded-3 p-3 px-4' style={{backgroundColor: '#FEF0E7'}}>
              <small>
                <span className='text-danger fw-bold'>Rule:</span>{' '}
                <span className='text-secondary'>Bill 99470 OR 99457 based on total time &mdash; not both. If &ge;20 minutes, bill 99457. Add 99458 for each additional 20 min (max 3 units).</span>
              </small>
            </div>
          </div>
        </div>

        {/* Key Takeaway - inside the guide card */}
        <div className='rounded-3 p-4 mt-4' style={{backgroundColor: '#FFF8E1'}}>
          <h6 className='fw-bold mb-2'>
            <i className='bi bi-stars me-1 text-warning'></i>
            Key Takeaway
          </h6>
          <p className='mb-2' style={{fontSize: '0.9rem', color: '#5f6368'}}>
            The new 2026 codes close gaps for patients who previously had no billable option. Patients
            with 2-15 reading days or 10-19 minutes of care can now generate revenue. These codes
            provide greater flexibility while maintaining compliance with CMS requirements.
          </p>
          <p className='mb-0 fw-semibold' style={{fontSize: '0.9rem', color: '#5f6368'}}>
            <i className='bi bi-exclamation-triangle me-1 text-warning'></i>
            For 99445: Document why shorter monitoring frequency is clinically appropriate.
          </p>
        </div>
      </div>
    </>
  )
}

export default BillingReport
